/**
 * Single source of truth for user-facing error codes.
 * API response: code = unique 3-digit catalog id per base string (100–999), errorId = throw site (1–999).
 * HTTP status is registry `status` only; do not infer HTTP from `code`.
 * Derived maps (ERROR_IDS_REGISTRY, ERROR_STRING_TO_CATALOG_CODE, ERROR_NUMERIC, USER_FACING_CODES)
 * come from this registry; USER_FACING_CODES = all registry keys (any can be shown to the user).
 */

export type ErrorRegistryEntry = {
  /** Stable unique API/catalog number (100–999); one per registry key. */
  catalogCode: number;
  status: number;
  userMessage: string;
};

/** Single registry: string id → { catalogCode, status, userMessage }. Add new codes here only. */
export const ERROR_REGISTRY: Record<string, ErrorRegistryEntry> = {
  E_TEST: {
    catalogCode: 101,
    status: 500,
    userMessage: 'Test error (test-only).',
  },
  E_UNKNOWN_500: {
    catalogCode: 102,
    status: 500,
    userMessage: 'An error occurred. Please try again.',
  },
  ERR_ADMIN_SETTINGS_CATCHALL_RENDER: {
    catalogCode: 103,
    status: 500,
    userMessage: 'This admin settings page could not be loaded.',
  },
  ERR_ADMIN_SETTINGS_LAYOUT_FORBIDDEN: {
    catalogCode: 104,
    status: 403,
    userMessage: 'Admin settings require administrator access.',
  },
  ERR_ADMIN_SETTINGS_MODULES_TREE_GET: {
    catalogCode: 105,
    status: 500,
    userMessage: 'Failed to load the module settings tree.',
  },
  ERR_ADMIN_SETTINGS_SECTION_NOT_FOUND: {
    catalogCode: 106,
    status: 404,
    userMessage: 'That admin settings section was not found.',
  },
  ERR_AUTH_401: {
    catalogCode: 107,
    status: 401,
    userMessage: 'Session expired or not authenticated. Please sign in again.',
  },
  ERR_BAD_REQUEST_400: {
    catalogCode: 108,
    status: 400,
    userMessage: 'Invalid request.',
  },
  ERR_CHAT_500: {
    catalogCode: 109,
    status: 500,
    userMessage: 'Internal server error during chat.',
  },
  ERR_DB_UNAVAILABLE_503: {
    catalogCode: 110,
    status: 503,
    userMessage: 'Database is unreachable. Start the database and retry.',
  },
  ERR_EDITOR_DOCUMENT_CREATE: {
    catalogCode: 111,
    status: 500,
    userMessage: 'Failed to create or load the editor document after create.',
  },
  ERR_EDITOR_DOCUMENT_LIST: {
    catalogCode: 112,
    status: 500,
    userMessage: 'Failed to list project documents for the workbench.',
  },
  ERR_EDITOR_SSE: {
    catalogCode: 113,
    status: 503,
    userMessage: 'Live updates connection failed. Refresh the page or check your network.',
  },
  ERR_EDITOR_THREADS_LOAD: {
    catalogCode: 114,
    status: 500,
    userMessage: 'Failed to load chat messages. Try again or refresh.',
  },
  ERR_EMBED_400: {
    catalogCode: 115,
    status: 400,
    userMessage: 'Invalid embed request. Check the request body and configuration.',
  },
  ERR_EMBED_401: {
    catalogCode: 116,
    status: 401,
    userMessage: 'Invalid or missing embed API key or host user token.',
  },
  ERR_EMBED_403: {
    catalogCode: 117,
    status: 403,
    userMessage: 'Origin not allowed for this embed instance.',
  },
  ERR_EMBED_429: {
    catalogCode: 118,
    status: 429,
    userMessage: 'Embed rate limit or session cooldown. Wait and retry.',
  },
  ERR_EMBED_500: {
    catalogCode: 119,
    status: 500,
    userMessage: 'Embed service error. Check server logs and persona configuration.',
  },
  ERR_FORBIDDEN_403: {
    catalogCode: 120,
    status: 403,
    userMessage: 'You do not have permission to perform this action.',
  },
  ERR_INTERNAL_500: {
    catalogCode: 121,
    status: 500,
    userMessage: 'An error occurred. Please try again.',
  },
  ERR_NODE_VERSION: {
    catalogCode: 122,
    status: 500,
    userMessage: 'Incompatible Node.js version. Check server logs for the required version.',
  },
  ERR_NOT_FOUND_404: {
    catalogCode: 123,
    status: 404,
    userMessage: 'Resource not found.',
  },
  ERR_WORKSPACE_FILES_LIST: {
    catalogCode: 124,
    status: 500,
    userMessage: 'Failed to load workspace files.',
  },
  ERR_WORKSPACE_STORAGE_PATH_MISSING: {
    catalogCode: 125,
    status: 500,
    userMessage: 'Workspace file storage is not configured. Set WORKSPACE_STORAGE_PATH in this target’s env file.',
  },
  PORTAL_API_KEY_NOT_FOUND: {
    catalogCode: 126,
    status: 401,
    userMessage: 'API key not found. Configure in Admin > Portal settings or set the provider API key environment variable.',
  },
  PORTAL_CONFIG_MISSING: {
    catalogCode: 127,
    status: 400,
    userMessage: 'Portal configuration is missing. Configure in Admin > Portal settings.',
  },
  PORTAL_EDITOR_PROMPT_NOT_CONFIGURED: {
    catalogCode: 128,
    status: 400,
    userMessage: 'Editor system prompt not configured. Configure in Admin Settings > Editor > Prompts or run pnpm db:seed.',
  },
  PORTAL_MODEL_NOT_SET: {
    catalogCode: 129,
    status: 400,
    userMessage: 'Model not configured. Set model in Admin > Portal or module settings.',
  },
  PORTAL_MODEL_PROFILE_NOT_FOUND: {
    catalogCode: 130,
    status: 400,
    userMessage: 'The resolved model profile is missing. Configure model profiles in Admin.',
  },
  PORTAL_PROVIDER_BAD_REQUEST: {
    catalogCode: 131,
    status: 400,
    userMessage: 'Provider rejected the request. Check model, temperature, token limits, or tool settings in Admin > Portal.',
  },
  PORTAL_PROVIDER_ERROR: {
    catalogCode: 132,
    status: 500,
    userMessage: 'Provider error. Check Admin > Portal settings and server logs.',
  },
  PORTAL_PROVIDER_RATE_LIMIT: {
    catalogCode: 133,
    status: 429,
    userMessage: 'Provider rate limit exceeded. Wait a moment or try a different model.',
  },
  PORTAL_PROVIDER_TOOL_USE_NOT_SUPPORTED: {
    catalogCode: 134,
    status: 404,
    userMessage: 'This model does not support tool use. Try a different model in Admin > Portal settings.',
  },
  PORTAL_PROVIDER_UNAVAILABLE: {
    catalogCode: 135,
    status: 503,
    userMessage: 'Provider temporarily unavailable. Try again shortly.',
  },
  PORTAL_PROVIDER_UNAUTHORIZED: {
    catalogCode: 136,
    status: 401,
    userMessage: 'Invalid or missing API key. Check Admin > Portal settings for the selected provider.',
  },
  RATE_LIMIT_429: {
    catalogCode: 137,
    status: 429,
    userMessage: 'Too many requests. Please try again later.',
  },
};

// Derived: same keys, only status + userMessage (for getErrorIdEntry)
export type ErrorIdEntry = { status: number; userMessage: string };
export const ERROR_IDS_REGISTRY: Record<string, ErrorIdEntry> = Object.fromEntries(
  Object.entries(ERROR_REGISTRY).map(([k, v]) => [k, { status: v.status, userMessage: v.userMessage }])
);

export const ERROR_STRING_TO_CATALOG_CODE: Record<string, number> = Object.fromEntries(
  Object.entries(ERROR_REGISTRY).map(([k, v]) => [k, v.catalogCode])
);

export const ERROR_NUMERIC: Record<string, number> = Object.fromEntries(
  Object.entries(ERROR_REGISTRY).map(([k, v]) => [k, v.status])
);

/** All registered codes. Any can be shown to the user; no separate "user-facing" subset. */
export const USER_FACING_CODES: readonly string[] = Object.keys(ERROR_REGISTRY);

const UNKNOWN_CATALOG_FALLBACK = ERROR_REGISTRY.E_UNKNOWN_500.catalogCode;

export function getErrorIdEntry(errorId: string): ErrorIdEntry | undefined {
  const direct = ERROR_IDS_REGISTRY[errorId];
  if (direct) return direct;
  const baseId = getBaseErrorId(errorId);
  return baseId ? ERROR_IDS_REGISTRY[baseId] : undefined;
}

/**
 * Resolve the registry base code for a location-specific errorId.
 * Handles composite id "BASE:uniqueThrowId" (from KorvoError) by returning BASE.
 * Also supports suffix: errorId.startsWith(key + '_').
 */
export function getBaseErrorId(errorId: string): string | undefined {
  const sep = ':';
  if (errorId.includes(sep)) {
    const base = errorId.slice(0, errorId.indexOf(sep));
    return base || undefined;
  }
  let best: string | undefined;
  for (const key of Object.keys(ERROR_IDS_REGISTRY)) {
    if (errorId === key || errorId.startsWith(key + '_')) {
      if (!best || key.length > best.length) best = key;
    }
  }
  return best;
}

export function getAllErrorIds(): string[] {
  return Object.keys(ERROR_IDS_REGISTRY);
}

/** Numeric `code` for JSON API bodies: unique catalog id per base string (see ERROR_REGISTRY). */
export function getErrorCatalogCode(errorIdString: string): number {
  const base = getBaseErrorId(errorIdString) ?? errorIdString;
  return ERROR_STRING_TO_CATALOG_CODE[base] ?? UNKNOWN_CATALOG_FALLBACK;
}

export function getErrorUniqueThrowId(errorIdString: string): number {
  const sep = ':';
  if (errorIdString.includes(sep)) {
    const num = parseInt(errorIdString.slice(errorIdString.indexOf(sep) + 1), 10);
    if (!Number.isNaN(num) && num >= 1 && num <= 999) return num;
  }
  return 1;
}
